import {scaledValue} from "../../performance/CompactPerformanceValue";

export class IdeaUpdateOutcome {
    constructor(status, portfolios) {
        this.status = status;
        this.portfolios = portfolios;
    }
}

export class PortfolioUpdateOutcome {
    constructor(portfolioId, portfolioName, status, investmentPercentChange, previousInvestment, updatedInvestment, failureMessage, suggestion) {
        this.portfolioId = portfolioId;
        this.portfolioName = portfolioName;
        this.status = status;
        this.investmentPercentChange = investmentPercentChange;
        this.previousInvestment = previousInvestment;
        this.updatedInvestment = updatedInvestment;
        this.failureMessage = failureMessage;
        this.suggestion = suggestion;
    }
}


export function suggestUserAction(validationFailures = []) {
    const suggestions = [];

    for (const failure of validationFailures) {
        switch (failure.code) {
            case "MaxShortInvestmentPerCompanyRule":
            case "MaxShortInvestmentPerAuthorRule":
                suggestions.push("short");
                break;
            case "MaxLongInvestmentPerCompanyRule":
            case "MaxLongInvestmentPerAuthorRule":
                suggestions.push("long");
                break;
            case "MaxInvestmentPerAuthorRule":
                suggestions.push("other");
                break;
            default:
                suggestions.push(null);
        }
    }

    if (suggestions.length === 0 || suggestions.includes(null))
        return null;
    else
        return suggestions.join("/");
}

function formatPortfolioRuleFailure(failure) {
    switch (failure.code) {
        case "MinTimeSinceLastChange":
            return `Min time since last change (${failure.value} trading hours)`;
        case "MaxInvestmentPerIdeaRule":
            return `Max investment (${scaledValue(failure.value)})`;
        case "MaxInvestmentPerAuthorRule":
            return `Max capital for author (${scaledValue(failure.value)})`;
        case "MinInvestmentPerIdeaRule":
            return `Min investment (${scaledValue(failure.value)})`;
        case "MaxInvestmentPerCompanyRule":
            return `Max capital for company (${scaledValue(failure.value)})`;
        case "MaxLongInvestmentPerCompanyRule":
            return `Max capital in long ideas for company (${scaledValue(failure.value)})`;
        case "MaxShortInvestmentPerCompanyRule":
            return `Max capital in short ideas for company (${scaledValue(failure.value)})`;
        case "MaxLongInvestmentPerAuthorRule":
            return `Max capital in long ideas for author (${scaledValue(failure.value)})`;
        case "MaxShortInvestmentPerAuthorRule":
            return `Max capital in short ideas for author (${scaledValue(failure.value)})`;
        case "MustMatchViewStateRule":
            return "Portfolio stock selection";
        case "MaxInvestmentPerIdeaAsADVPercentRule":
            return `Max ADV (${failure.value}%)`;
        default:
            return null;
    }
}

export function failureMessageFrom(validationFailures = []) {
    const blockingFailures = validationFailures.filter(f => f.blocking);
    if (blockingFailures.length > 0) {
        const blockingFailure = blockingFailures[0];
        switch (blockingFailure.code) {
            case "IDEA_NOT_OPEN":
                return "Idea is not open";
            case "NOT_AUTHORIZED":
                return "Not authorised to update idea";
            case "TRADE_TONE_VIOLATION":
                return "Limit up/down violation";
            default:
                const failureText = formatPortfolioRuleFailure(blockingFailure);
                return failureText ? `Blocked by portfolio rule: ${failureText}` : null;
        }
    }
    if (validationFailures.length > 0) {
        const failure = validationFailures[0];
        if (failure.code === "SYSTEM_ADV") {
            return `Limited by system ADV (${failure.value}%)`;
        }
        const failureText = formatPortfolioRuleFailure(failure);
        return failureText ? `Limited by portfolio rule: ${failureText}` : null;
    }
    return null;
}

function classifyPortfolioOutcome(portfolio, requestedPercentage) {
    const blockingFailures = portfolio.validationFailures.filter(vf => vf.blocking);
    if (blockingFailures.length > 0) {
        return new PortfolioUpdateOutcome(portfolio.portfolioId, portfolio.portfolioName, "failed", portfolio.actualUpdatePercent, portfolio.amountBefore, portfolio.amountAfter, failureMessageFrom(portfolio.validationFailures), suggestUserAction(portfolio.validationFailures));
    }
    else if (portfolio.validationFailures.length > 0 || isPartialPercentageUpdate(requestedPercentage, portfolio.actualUpdatePercent, portfolio.amountBefore)) {
        return new PortfolioUpdateOutcome(portfolio.portfolioId, portfolio.portfolioName, "partial", portfolio.actualUpdatePercent, portfolio.amountBefore, portfolio.amountAfter, failureMessageFrom(portfolio.validationFailures), suggestUserAction(portfolio.validationFailures));
    }
    else {
        return new PortfolioUpdateOutcome(portfolio.portfolioId, portfolio.portfolioName, "success", portfolio.actualUpdatePercent, portfolio.amountBefore, portfolio.amountAfter, null, null);
    }
}

function isPartialPercentageUpdate(requestedPercentage, actualUpdatePercent, amountBefore) {
    return Math.round(amountBefore * (1 + requestedPercentage)) !== Math.round(amountBefore * (1 + actualUpdatePercent))
}

function headlineStatus(portfolioOutcomes) {
    if (portfolioOutcomes.filter(p => p.status === "failed").length === portfolioOutcomes.length) return "failed";
    if (portfolioOutcomes.filter(p => p.status === "success").length === portfolioOutcomes.length) return "success";
    return "partial";
}

export function classifyUpdateOutcomes(outcomes, requestedPercentage) {
    const portfolioOutcomes = [];

    for (const portfolioOutcome of outcomes) {
        portfolioOutcomes.push(classifyPortfolioOutcome(portfolioOutcome, requestedPercentage));
    }

    return new IdeaUpdateOutcome(headlineStatus(portfolioOutcomes), portfolioOutcomes);
}
